{
  "title": "How to reserve an event shuttle seat",
  "description": "Reserve a free shuttle seat on the event page, with just an email address or after signing in, and get a ride QR.",
  "headings": [
    "What you will accomplish",
    "Who this is for",
    "Before you start",
    "Steps",
    "Afterward",
    "FAQ"
  ],
  "summary": "When done you will hold a confirmed (or waitlisted) shuttle seat and find your ride QR under My shuttles; show it to staff when boarding.",
  "roleNote": "Event attendees (guests, professors, students and partners alike); you do not need to be a competition participant",
  "prerequisites": [
    "The event address, for example /events/taai-2026, or open Events in the main navigation",
    "An email address you can read (email-only booking) or an account with a verified email (signed-in booking)",
    "Each direction on each day allows one booking, so decide the day, direction and trip first"
  ],
  "pathLabel": "Main navigation → Events → choose the event → Free shuttle reservation (/events/:slug/shuttle)",
  "steps": [
    {
      "title": "1. Open the booking page and read the trips",
      "description": "On the event page press \"Free shuttle reservation\" at the top. The booking page has one tab per service day (today in Taipei time by default) and always shows Taiwan time (UTC+08:00). Each trip card shows the trip code, the departure and arrival stations, and seat status (seats available, few left, full with waitlist, not open yet). Trip codes work like high-speed-rail train numbers: three digits for the day (1 = 11/20, 2 = 11/21), the departure station and the arrival station; stations are 1 = NTPU, 2 = Banqiao HSR, 3 = venue. For example 113 is day one from NTPU to the venue. Use the in-page 中文 / English switch to change language."
    },
    {
      "title": "2. Choose how to book",
      "description": "Signed in with a verified email: press Reserve a seat on the trip. Not signed in: press \"Book with email only (no account)\", enter your real name and email, tick \"I confirm this is my real name\", complete the verification and press \"Email me a confirmation link\". You can also use Google sign-in, which returns you to the same page. An email-only request holds no seat until you open the emailed link."
    },
    {
      "title": "3. First booking: rider contact details",
      "description": "Your first booking asks for your real name (required, 2 to 60 characters), organization and job title (optional) and a mobile number (optional, only for shuttle changes and urgent on-site contact), plus a tick confirming the name is real. On-site check-in staff see only your name, organization and job title; the mobile number is stored encrypted, only the event owner can view it for urgent contact and every view is logged; all of it is deleted 30 days after the event. Saving completes the booking and you are not asked again for the same event."
    },
    {
      "title": "4. Add companions (optional)",
      "description": "Press \"Reserve together with companions\" to add up to 3 companions, 4 seats in total including you. Each companion needs a real name, gets their own ride QR, can be cancelled individually and is waitlisted individually. A companion's organization, job title and phone are not copied from yours."
    },
    {
      "title": "5. Check the result and get your QR",
      "description": "After a successful booking the trip shows Reserved and \"Seat reserved\". A full trip shows \"Joined waitlist, position #N\"; when someone cancels, the first person on the waitlist is promoted automatically and emailed. For email-only booking, open the confirmation link within 30 minutes (it works once); opening it creates the booking and signs you in. Open My shuttles to see the boarding-pass card and ride QR; show it to staff when boarding and do not forward it. Waitlist entries have no QR."
    },
    {
      "title": "6. Cancel, calendar and reminder",
      "description": "Press Cancel reservation on the timetable or in My shuttles and confirm. Booking and cancellation close 12 hours before departure; after that contact the organizer. A confirmed booking can be added to your calendar as an .ics file (it contains no QR). To get a reminder tick \"Email me a reminder within 24 hours before departure\" in My shuttles; it is off by default, can be turned off at any time and never contains the ride QR."
    }
  ],
  "afterward": [
    "One active booking (confirmed or waitlisted) per person, per day, per direction",
    "My itinerary at the top of the event page shows how many trips you have booked and waitlisted",
    "Within 24 hours of departure the event page shows a Next shuttle card with your QR"
  ],
  "nextSteps": "If booking fails see \"Cannot reserve an event shuttle seat\"; for meals, banquet reply and the help desk see \"How to reply about meals and use the help desk\".",
  "faq": [
    {
      "q": "Does the shuttle cost anything?",
      "a": "No, it is free. Registration, payment and banquet entitlement are always governed by the official TAAI registration system; this platform only provides supplementary service information."
    },
    {
      "q": "Can I book for other people?",
      "a": "Yes, as companions: up to 3, each with a real name, checked against the passenger list on site. Do not forward QR codes."
    },
    {
      "q": "What if a trip is full?",
      "a": "Join the waitlist. When someone cancels, the first person on the waitlist is promoted automatically and notified by email; your position shows in My shuttles."
    },
    {
      "q": "Why do you need my real name?",
      "a": "Staff check boarders against the passenger list. The system cannot verify that a name is genuine; it relies on your explicit confirmation and on-site checks."
    }
  ],
  "figures": [
    {
      "name": "shuttle-booking",
      "alt": "The booking page: pick a day tab and trip; each card shows trip code, departure and arrival stations and seat status",
      "caption": "The booking page: pick a day tab and trip; each card shows trip code, departure and arrival stations and seat status"
    },
    {
      "name": "my-shuttle",
      "alt": "My shuttles: boarding-pass card, ride QR, calendar, reminder and cancel",
      "caption": "My shuttles: boarding-pass card, ride QR, calendar, reminder and cancel"
    }
  ]
}
